
import React from 'react';
import { Link } from 'react-router-dom';
import { directoryBusinesses } from '../../../../mocks/directory';

interface Business {
  id: number;
  name: string;
  description: string;
  category: string;
  rating: number;
  verified?: boolean;
  image?: string;
  logo?: string;
  address?: string;
}

interface SimilarBusinessesProps {
  currentId: number;
  category: string;
}

/**
 * Returns an array of up to three businesses that are similar to the current one.
 * If there are not enough businesses in the same category we fill the remaining
 * slots with the highest‑rated businesses (excluding the current one).
 */
export default function SimilarBusinesses({
  currentId,
  category,
}: SimilarBusinessesProps) {
  // Guard against unexpected data shapes
  if (!Array.isArray(directoryBusinesses)) {
    console.error('directoryBusinesses is not an array');
    return null;
  }

  const similar: Business[] = directoryBusinesses
    .filter((b) => b.id !== currentId && b.category === category)
    .slice(0, 3);

  const extras: Business[] =
    similar.length < 3
      ? directoryBusinesses
          .filter(
            (b) =>
              b.id !== currentId &&
              !similar.find((s) => s.id === b.id) &&
              b.rating !== undefined
          )
          .sort((a, b) => (b.rating ?? 0) - (a.rating ?? 0))
          .slice(0, 3 - similar.length)
      : [];

  const all: Business[] = [...similar, ...extras];

  // If nothing to show, render nothing
  if (all.length === 0) {
    return null;
  }

  return (
    <section className="py-14 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-center justify-between mb-8">
          <h2 className="text-2xl font-bold text-gray-900">
            Similar Businesses
          </h2>
          <Link
            to="/directory"
            className="text-sm font-semibold text-teal-600 hover:text-teal-700 transition-colors cursor-pointer flex items-center gap-1"
          >
            View All{' '}
            <i className="ri-arrow-right-line text-sm" aria-hidden="true"></i>
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {all.map((biz) => (
            <Link
              key={biz.id}
              to={`/directory/${biz.id}`}
              className="group bg-gray-50 rounded-2xl overflow-hidden hover:shadow-lg transition-all cursor-pointer border border-gray-100"
            >
              <div className="relative h-44 overflow-hidden">
                {/* Fallback image handling */}
                <img
                  src={biz.image ?? '/fallback-image.png'}
                  alt={`${biz.name} - Roseville business`}
                  title={`${biz.name} Roseville CA`}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute top-3 right-3 px-2.5 py-1 bg-white/90 backdrop-blur-sm rounded-full flex items-center gap-1">
                  <i className="ri-star-fill text-amber-400 text-xs" aria-hidden="true"></i>
                  <span className="text-xs font-bold text-gray-900">
                    {biz.rating?.toFixed(1) ?? 'N/A'}
                  </span>
                </div>
                {biz.verified && (
                  <div className="absolute top-3 left-3">
                    <span className="px-2 py-1 bg-teal-600 text-white text-[10px] font-bold rounded-full whitespace-nowrap">
                      <i
                        className="ri-verified-badge-fill text-[10px] mr-0.5"
                        aria-hidden="true"
                      ></i>{' '}
                      Verified
                    </span>
                  </div>
                )}
              </div>

              <div className="p-5">
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-7 h-7 bg-teal-50 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i
                      className={`${biz.logo ?? ''} text-teal-600 text-sm`}
                      aria-hidden="true"
                    ></i>
                  </div>
                  <h4 className="text-sm font-bold text-gray-900 group-hover:text-teal-600 transition-colors truncate">
                    {biz.name}
                  </h4>
                </div>

                <p className="text-xs text-gray-500 mt-1 line-clamp-2">
                  {biz.description}
                </p>

                <div className="flex items-center gap-2 mt-3 text-xs text-gray-400">
                  <i className="ri-map-pin-line text-teal-500" aria-hidden="true"></i>
                  <span className="truncate">{biz.address}</span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
